import { FormInstance } from 'antd';
import { StateAttachment, TemplateModalFormValues } from 'components/TemplatesModal/TemplateModal.types';
import React, { MutableRefObject, useRef, useState } from 'react';
import { useStore } from 'stores/RootStore';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';
import { TemplateModalQuery } from 'utils/graphql/queries/__generated__/templateModal';
import { useUsers } from 'utils/hooks/useUsers';

import { useRenderedTemplateSync } from './useRenderedTemplateSync';
import { useTemplatesModalFormDerived } from './useTemplatesModalFormDerived';
import { useTemplatesModalFormHandlers } from './useTemplatesModalFormHandlers';
import { useTemplatesModalFormInitialization } from './useTemplatesModalFormInitialization';

interface Args {
  form: FormInstance<TemplateModalFormValues>;
  changesMadeRef?: MutableRefObject<boolean>;
  templateModalData: TemplateModalQuery;
  selectedCategoryId: string | null;
  setSelectedCategoryId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedTemplate: TemplateFragment | null;
  setTemplateGroupId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedTemplate: React.Dispatch<React.SetStateAction<TemplateFragment | null>>;
}

export const useTemplatesModalForm = ({
  form,
  changesMadeRef,
  templateModalData,
  selectedCategoryId,
  setSelectedCategoryId,
  selectedTemplate,
  setTemplateGroupId,
  setSelectedTemplate,
}: Args) => {
  const isInitialisedAfterCategoryChange = useRef(false);
  const [emailFieldsVisible, setEmailFieldsVisible] = useState(false);
  const [attachments, setAttachments] = useState<StateAttachment[]>([]);

  const { templatesModalStore } = useStore();
  const { getUserById } = useUsers();

  const isVisibleToClient = templatesModalStore.messageVisibilityAttributes.isVisibleToClient;
  const isVisibleToPartner = templatesModalStore.messageVisibilityAttributes.isVisibleToPartner;

  const {
    category,
    selectedTemplateGroup,
    actionableActions,
    hasActionableMessages,
    currentFormLocale,
    templateMapByConstantName,
    clientOrPartnerLocale,
    localeOptions,
    isEmailSendingAvailable,
    isUploadingFilesAvailable,
    bulkCategoryId,
  } = useTemplatesModalFormDerived({
    form,
    templateModalData,
    selectedCategoryId,
    isVisibleToClient,
    isVisibleToPartner,
  });

  const {
    handleTemplateSelect,
    handleCategoryChange,
    handleTemplateConstantNameChange,
    handleLocaleChange,
    handleMentionSelect,
    handleMentionDeselect,
    handleToChange,
    handleCcChange,
    handleBccChange,
  } = useTemplatesModalFormHandlers({
    form,
    changesMadeRef,
    clientOrPartnerLocale,
    templateMapByConstantName,
    setSelectedCategoryId,
    setSelectedTemplate,
    setAttachments,
    setMessageVisibleTo: templatesModalStore.setMessageVisibleTo,
  });

  const { renderedTemplateData, renderedTemplateLoading } = useRenderedTemplateSync({
    form,
    selectedTemplate,
    sourceId: templatesModalStore.messageSourceForTemplates?.sourceId,
    sourceType: templatesModalStore.messageSourceForTemplates?.sourceType,
    setAttachments,
  });

  useTemplatesModalFormInitialization({
    form,
    templateModalData,
    category,
    selectedTemplateGroup,
    bulkCategoryId,
    isBulkActions: templatesModalStore.isBulkActions,
    isVisibleToClient,
    isVisibleToPartner,
    isInitialisedAfterCategoryChange,
    setSelectedCategoryId,
    setTemplateGroupId,
    handleCategoryChange,
    handleTemplateConstantNameChange,
    handleTemplateSelect,
  });

  return {
    emailFieldsVisible,
    setEmailFieldsVisible,
    getUserById,
    templateMapByConstantName,
    renderedTemplateData,
    renderedTemplateLoading,
    isEmailSendingAvailable,
    isUploadingFilesAvailable,
    actionableActions,
    localeOptions,
    currentFormLocale,
    attachments,
    setAttachments,
    handleLocaleChange,
    handleMentionSelect,
    handleMentionDeselect,
    handleToChange,
    handleCcChange,
    handleBccChange,
    handleCategoryChange,
    handleTemplateConstantNameChange,
    hasActionableMessages,
    isBulkActions: templatesModalStore.isBulkActions,
  };
};
